import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../components/Card";
import Modal from "../../components/Modal";
import Chip from "../../components/Dashboard/Chip";
import { cancelReward, grantManualReward, listRewards, retryReward } from "../../api/referrals.admin.service";
import {
  askReason,
  CursorPager,
  errorCodeOf,
  errorDetailsOf,
  isOk,
  StatusBadge,
  useCursorList,
  useErrorText,
  useFormat,
} from "./shared";
import styles from "./Referrals.module.sass";

const KINDS = ["WELCOME", "INVITER", "MANUAL"];
const STATUSES = ["PENDING", "APPLIED", "BANKED", "FAILED", "CAPPED", "NOT_APPLICABLE", "CANCELLED"];
const CANCELLABLE = ["PENDING", "FAILED", "BANKED"];

/**
 * Recompensas (06 §2.2 acciones, A2.8): bienvenidas, meses de referente y
 * manuales. Cancelar o reintentar con motivo; alta de recompensa manual.
 */
const RewardsTab = () => {
  const { t } = useTranslation();
  const fmt = useFormat();
  const errorText = useErrorText();
  const [filters, setFilters] = useState({ kind: "", status: "", userId: "" });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const list = useCursorList(listRewards, { ...filters, limit: 30 });

  const toggle = (key, value) => setFilters((f) => ({ ...f, [key]: f[key] === value ? "" : value }));

  const act = async (fn) => {
    const resp = await fn();
    if (!isOk(resp)) setError(errorCodeOf(resp));
    else {
      setError(null);
      list.reload();
    }
  };

  return (
    <>
      <Card
        className={styles.card}
        title={t("referrals.rewards.title")}
        classTitle="title-green"
        head={
          <button type="button" className="button button-small" onClick={() => setCreating(true)}>
            + {t("referrals.rewards.manual")}
          </button>
        }
      >
        <div className={styles.filters}>
          {KINDS.map((k) => (
            <Chip key={k} on={filters.kind === k} onClick={() => toggle("kind", k)}>
              {t(`referrals.rewards.kind_${k}`)}
            </Chip>
          ))}
          <span className={styles.sep} />
          {STATUSES.map((s) => (
            <Chip key={s} on={filters.status === s} onClick={() => toggle("status", s)}>
              {t(`referrals.status.${s}`)}
            </Chip>
          ))}
          <input
            className={styles.search}
            placeholder={t("referrals.rewards.userId")}
            value={filters.userId}
            onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value.trim() }))}
          />
        </div>
        {error && <div className={styles.errorBox}>{errorText(error)}</div>}
        {list.error && <div className={styles.empty}>{errorText(list.error)}</div>}
        {!list.error && list.items.length === 0 && (
          <div className={styles.empty}>{list.loading ? t("referrals.common.loading") : t("referrals.common.noData")}</div>
        )}
        <div className={styles.table}>
          {list.items.map((r) => (
            <div key={r.id} className={styles.row}>
              <code className={styles.codeCell}>{r.days} d</code>
              <div className={styles.rowMain}>
                <div className={styles.rowTitle}>
                  {t(`referrals.rewards.kind_${r.kind}`)} · {r.beneficiary?.email || r.beneficiary?.name || r.beneficiaryUserId}
                </div>
                <div className={styles.muted}>
                  {r.channel ? `${t(`referrals.rewards.channel_${r.channel}`, { defaultValue: r.channel })} · ` : ""}
                  {r.status === "APPLIED" && r.appliedUntil
                    ? t("referrals.rewards.until", { date: fmt.date(r.appliedUntil) })
                    : t("referrals.rewards.due", { date: fmt.dateTime(r.dueAt) })}
                  {r.lastError ? ` · ${r.lastError}` : ""}
                  {r.reason ? ` · ${r.reason}` : ""}
                </div>
              </div>
              <div className={styles.rowSide}>
                <StatusBadge status={r.status} />
                <div className={styles.rowActions}>
                  {r.status === "FAILED" && (
                    <button type="button" className={styles.linkBtn} onClick={() => act(() => retryReward(r.id))}>
                      {t("referrals.rewards.retry")}
                    </button>
                  )}
                  {CANCELLABLE.includes(r.status) && (
                    <button
                      type="button"
                      className={styles.linkBtnDanger}
                      onClick={() => {
                        const reason = askReason(t);
                        if (reason) act(() => cancelReward(r.id, reason));
                      }}
                    >
                      {t("referrals.rewards.cancel")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <CursorPager list={list} />
      </Card>

      <Modal visible={creating} onClose={() => setCreating(false)} outerClassName={styles.modalOuter}>
        {creating && (
          <ManualRewardForm
            onDone={() => {
              setCreating(false);
              list.reload();
            }}
          />
        )}
      </Modal>
    </>
  );
};

const ManualRewardForm = ({ onDone }) => {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [form, setForm] = useState({ userId: "", days: "30", reason: "" });
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [saving, setSaving] = useState(false);
  const field = (key) => ({ value: form[key], onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })) });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const resp = await grantManualReward(form.userId.trim(), Number(form.days), form.reason.trim());
    setSaving(false);
    if (isOk(resp)) onDone();
    else {
      setError(errorCodeOf(resp));
      setDetails(errorDetailsOf(resp));
    }
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <h3 className={styles.detailTitle}>{t("referrals.rewards.manual")}</h3>
      <div className={styles.muted}>{t("referrals.rewards.manualHint")}</div>
      <label>
        {t("referrals.rewards.userId")}
        <input required {...field("userId")} placeholder="64f…" />
      </label>
      <label>
        {t("referrals.rewards.days")}
        <input required type="number" min="1" max="365" {...field("days")} />
      </label>
      <label>
        {t("referrals.common.reason")}
        <input required {...field("reason")} />
      </label>
      {error && (
        <div className={styles.errorBox}>
          {errorText(error)}
          {details.map((d) => (
            <div key={d.path + d.message}>
              <code>{d.path}</code>: {d.message}
            </div>
          ))}
        </div>
      )}
      <button type="submit" className="button" disabled={saving}>
        {t("referrals.rewards.grant")}
      </button>
    </form>
  );
};

export default RewardsTab;
